import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DiscordLogo, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import ServerStatsWidget from "../components/ServerStatsWidget";

const BG = "https://images.unsplash.com/photo-1514439827219-9137a0b99245?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxuaWdodCUyMGNpdHklMjBza3lsaW5lJTIwbmVvbiUyMGRhcmt8ZW58MHx8fHwxNzg1OTAxOTc3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const handleDiscord = async () => {
    try {
      const origin = window.location.origin;
      const res = await api.get("/auth/discord/url", { params: { origin } });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Discord OAuth ist noch nicht konfiguriert. Bitte den Owner um Setup bitten oder Demo-Login nutzen.");
    }
  };

  const handleDemo = async (e) => {
    e.preventDefault();
    if (!username.trim()) { toast.error("Username eingeben"); return; }
    setBusy(true);
    try {
      const u = await login(username.trim(), email.trim() || null);
      toast.success(`Willkommen zurück, ${u.username}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const quickPick = async (name, mail) => {
    setBusy(true);
    try {
      const u = await login(name, mail);
      toast.success(`Eingeloggt als ${u.username}`);
      nav("/dashboard");
    } catch (e) { toast.error("Fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative grain overflow-hidden">
      <div className="absolute inset-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-black/75 scanline" />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left brand column */}
        <div className="p-6 md:p-10 lg:p-16 flex flex-col justify-between border-r border-tactical-border/50 min-h-[40vh] lg:min-h-screen">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-white/20 flex items-center justify-center bg-black/60 backdrop-blur">
              <ShieldCheck size={22} weight="duotone" className="text-volt" />
            </div>
            <div className="font-mono text-xs tracking-[0.3em] text-white/60 uppercase">Routine RP // User Control</div>
          </div>

          <div className="stagger">
            <div className="font-mono text-xs text-volt tracking-widest mb-4">// SECURE ACCESS TERMINAL</div>
            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-7xl tracking-tight leading-[0.95]">
              Dein Char.<br/>
              <span className="text-volt">Deine Kontrolle.</span>
            </h1>
            <p className="mt-6 text-white/60 max-w-md text-base md:text-lg leading-relaxed">
              Fahrzeuge, Immobilien, Inventar, Akte und Statistiken deines Chars auf dem Routine Roleplay Server — jederzeit einsehbar.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md font-mono text-xs">
              <div className="border border-tactical-border p-3">
                <div className="text-white/40 uppercase tracking-wider">Server</div>
                <div className="text-white font-bold mt-1">ROUTINE.RP</div>
              </div>
              <div className="border border-tactical-border p-3">
                <div className="text-white/40 uppercase tracking-wider">Framework</div>
                <div className="text-white font-bold mt-1">ESX LEGACY</div>
              </div>
              <div className="border border-tactical-border p-3">
                <div className="text-white/40 uppercase tracking-wider">Status</div>
                <div className="text-tactical-success font-bold mt-1">● ONLINE</div>
              </div>
            </div>

            <div className="mt-8 max-w-lg">
              <ServerStatsWidget />
            </div>
          </div>

          <div className="font-mono text-[10px] text-white/30 tracking-widest">
            v1.0.0 — © 2026 ROUTINE ROLEPLAY
          </div>
        </div>

        {/* Right auth column */}
        <div className="p-6 md:p-10 lg:p-16 flex items-center">
          <div className="w-full max-w-md backdrop-blur-2xl bg-black/60 border border-tactical-border p-6 md:p-8 lg:p-10">
            <div className="font-mono text-xs text-white/50 tracking-widest mb-2">01 / LOGIN</div>
            <h2 className="font-display font-bold text-3xl mb-1">Anmelden</h2>
            <p className="text-white/50 text-sm mb-8">Verifiziere dich mit Discord oder nutze den Demo-Zugang.</p>

            <button
              data-testid="discord-login-btn"
              onClick={handleDiscord}
              className="w-full flex items-center justify-center gap-3 bg-[#5865F2] hover:bg-[#4752c4] text-white font-semibold py-3.5 px-6 border border-[#5865F2] hover:border-[#4752c4] transition-colors duration-150"
            >
              <DiscordLogo size={22} weight="fill" />
              <span>Mit Discord anmelden</span>
            </button>

            <div className="my-6 flex items-center gap-3 text-white/30 font-mono text-[10px] uppercase tracking-widest">
              <div className="flex-1 h-px bg-tactical-border" />
              oder demo-login
              <div className="flex-1 h-px bg-tactical-border" />
            </div>

            <form onSubmit={handleDemo} className="space-y-4">
              <div>
                <label className="block text-xs font-mono text-white/50 uppercase tracking-wider mb-2">Username</label>
                <input
                  data-testid="demo-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-black/60 border border-tactical-border focus:border-volt outline-none text-white px-4 py-3 font-mono text-sm transition-colors duration-150"
                  placeholder="Dein RP Name"
                />
              </div>
              <div>
                <label className="block text-xs font-mono text-white/50 uppercase tracking-wider mb-2">Email <span className="text-white/30">(optional, für Owner-Zugriff)</span></label>
                <input
                  data-testid="demo-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  className="w-full bg-black/60 border border-tactical-border focus:border-volt outline-none text-white px-4 py-3 font-mono text-sm transition-colors duration-150"
                  placeholder="you@example.com"
                />
              </div>
              <button
                data-testid="demo-login-submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-volt hover:text-white font-semibold py-3.5 px-6 border border-white hover:border-volt transition-colors duration-150 disabled:opacity-50"
              >
                {busy ? "Verbinde..." : "Terminal betreten"}
                <ArrowRight size={18} weight="bold" />
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-tactical-border">
              <div className="text-xs font-mono text-white/40 uppercase tracking-wider mb-3">Quick-Test</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="quick-kenji"
                  onClick={() => quickPick("Kenji Tanaka", "kenji@routine.rp")}
                  className="text-left border border-tactical-border hover:border-volt p-3 transition-colors duration-150"
                >
                  <div className="font-mono text-[10px] text-white/40">POLICE</div>
                  <div className="text-sm text-white">Kenji Tanaka</div>
                </button>
                <button
                  data-testid="quick-marco"
                  onClick={() => quickPick("Marco Vitelli", "marco@routine.rp")}
                  className="text-left border border-tactical-border hover:border-volt p-3 transition-colors duration-150"
                >
                  <div className="font-mono text-[10px] text-white/40">MECHANIC</div>
                  <div className="text-sm text-white">Marco Vitelli</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
